import { asciiList, regAscii } from '../constants/emoji';

const convert = (unicode: string) => {
    if (unicode.indexOf('-') > -1) {
        const parts = [];

        const s = unicode.split('-');

        for (let i = 0; i < s.length; i++) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            let part: number | string = parseInt(s[i]!, 16);

            if (part >= 0x10000 && part <= 0x10ffff) {
                const hi = Math.floor((part - 0x10000) / 0x400) + 0xd800;
                const lo = ((part - 0x10000) % 0x400) + 0xdc00;

                part = String.fromCharCode(hi) + String.fromCharCode(lo);
            } else {
                part = String.fromCharCode(part);
            }

            parts.push(part);
        }

        return parts.join('');
    }

    const s = parseInt(unicode, 16);

    if (s >= 0x10000 && s <= 0x10ffff) {
        const hi = Math.floor((s - 0x10000) / 0x400) + 0xd800;
        const lo = ((s - 0x10000) % 0x400) + 0xdc00;

        return String.fromCharCode(hi) + String.fromCharCode(lo);
    }

    return String.fromCharCode(s);
};

export const unescapeHTML = (text: string) => {
    const unescaped: { [key: string]: string } = {
        '&amp;': '&',
        '&#38;': '&',
        '&#x26;': '&',
        '&lt;': '<',
        '&#60;': '<',
        '&#x3C;': '<',
        '&gt;': '>',
        '&#62;': '>',
        '&#x3E;': '>',
        '&quot;': '"',
        '&#34;': '"',
        '&#x22;': '"',
        '&apos;': "'",
        '&#39;': "'",
        '&#x27;': "'",
    };

    return text.replace(
        /&(?:amp|#38|#x26|lt|#60|#x3C|gt|#62|#x3E|apos|#39|#x27|quot|#34|#x22);/gi,
        (match) => unescaped[match] ?? match,
    );
};

export const escapeHTML = (text: string) => {
    const escaped: { [key: string]: string } = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&apos;',
    };

    return text.replace(/[&<>"']/g, (match) => escaped[match] ?? match);
};

export interface AsciiSmileyConversion {
    /** The original ASCII smiley as it appeared in the input (e.g. ":-)"). */
    original: string;
    /** The resulting Unicode emoji (e.g. "🙂"). */
    emoji: string;
}

export interface ConvertEmojisToUnicodeOptions {
    /**     * Optional callback invoked for every ASCII smiley that was successfully     * converted to its Unicode emoji counterpart. Conversions are reported in     * the order they were processed. Used by the `EmojiInput` component to     * enable the "Backspace reverts the last auto-converted smiley" UX.     */
    onAsciiConversion?: (conversion: AsciiSmileyConversion) => void;
}

export const convertEmojisToUnicode = (
    text: string,
    regShortnames: RegExp,
    shortNameList: { [p: string]: string },
    options?: ConvertEmojisToUnicodeOptions,
): string => {
    let result = text;

    result = result.replace(/https?:\/\/.*?(?=$|\s)/gi, (fullMatch) =>
        fullMatch.replace(/:/g, '%3A'),
    );

    result = result.replace(regShortnames, (shortname) => {
        if (shortname) {
            const unicode = shortNameList[shortname];

            if (unicode) {
                return convert(unicode.toUpperCase());
            }
        }

        return shortname;
    });

    result = result.replace(/https?%3A\/\/.*?(?=$|\s)/gi, (fullMatch) =>
        fullMatch.replace(/%3A/g, ':'),
    );

    result = result.replace(regAscii, (fullMatch, m1, m2, m3) => {
        if (typeof m3 === 'string' && m3 !== '') {
            const unicode = asciiList[unescapeHTML(m3)];

            if (unicode) {
                const emoji = convert(unicode.toUpperCase());

                if (typeof options?.onAsciiConversion === 'function') {
                    options.onAsciiConversion({ original: m3, emoji });
                }

                return (m2 as string) + emoji;
            }
        }

        return fullMatch;
    });

    return result;
};

export const addSkinToneToEmoji = (
    emojiList: { [x: string]: { skin_tone_support: any } },
    emoji: string,
    skinTone: string,
): string =>
    emoji
        .split('\u{200D}')
        .map((rawEmoji) => {
            const parts = [rawEmoji.replace(/\ufe0f/, '')];

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (emojiList[rawEmoji]?.skin_tone_support) {
                parts.push(skinTone);
            }

            return parts.join('');
        })
        .join('\u{200D}');
